import React, {useEffect, useState} from 'react';
import {useId} from "@fluentui/react-hooks";
import {useAuth} from "react-oidc-context";
import styles from './ViewRequests.module.css';
import {
    Callout,
    CommandBarButton,
    IColumn,
    MessageBarType,
    Pivot,
    PivotItem,
    Spinner,
    SpinnerSize,
    Stack,
    StackItem
} from "@fluentui/react";
import strings from "../../../loc/strings";
import FilteredDetailsListWithPagination from "../FilteredDetailsListWithPagination/FilteredDetailsListWithPagination";
import {_activeColumns, _draftColumns, _incomingColumns, _outgoingColumns, Tab} from "./ViewRequestsUtils";
import {
    excelExportActive,
    fetchActiveMetrics,
    fetchActiveRequests, fetchTabMetrics
} from "../../../services/RequestServices/ActiveRequestService";
import {IDropdownOption} from "@fluentui/react/lib/Dropdown";
import {RequestsWithMetrics} from "../../../model/RequestsWithMetrics";
import EiopaFnPIcons from "../../EiopaFnPIcons/EiopaFnPIcons";
import {
    ROLE_CA_END_USER,
    ROLE_ESA_END_USER,
    SORT_ORDER_ASC,
    SORT_ORDER_DESC
} from "../../../constants/EiopaFnPConstants";
import {
    cancelOutgoingRequest,
    excelExportOutgoing,
    fetchOutgoingRequests,
    sendReminder
} from "../../../services/RequestServices/OutgoingRequestService";
import {excelExportIncoming, fetchIncomingRequests} from "../../../services/RequestServices/IncomingRequestService";
import {
    deleteDraftRequest,
    excelExportDraft,
    fetchDraftRequests
} from "../../../services/RequestServices/DraftRequestService";
import {displayMessageBar} from "../../LayoutWithAuth/LayoutWithAuth";
import {getUserInfo} from "../../../services/UserInfoService";
import FilterRequests from "../FilterRequests/FilterRequests";
import {IRequestFilters, RequestFilters} from "../../../model/RequestFilters";
import {ActiveMetrics} from "../../../model/ActiveMetrics";
import {TabMetrics} from "../../../model/TabMetrics";
import {ICARequest} from "../../../model/ICARequest";

function ViewRequests() {
    const auth = useAuth();

    const [filters, setFilters] = useState<IRequestFilters>(new RequestFilters());
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isFiltered, setIsFiltered] = useState<boolean>(false);
    const [selectedTab, setSelectedTab] = useState<string>(Tab.Active);
    const [isMetricsCalloutVisible, setIsMetricsCalloutVisible] = useState<boolean>(false);
    const [columnsState, setColumnsState] = useState<IColumn[]>(_activeColumns);
    const [requests, setRequests] = useState<ICARequest[]>([]);
    const [tabMetrics, setTabMetrics] = useState<TabMetrics>(new TabMetrics());
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10);
    const [activeMetrics, setActiveMetrics] = useState<ActiveMetrics>(new ActiveMetrics());
    const [isActiveMetricsLoading, setIsActiveMetricsLoading] = useState<boolean>(true);
    const [bool, setBool] = useState<boolean>(false);
    const [resetFlag, setResetFlag] = useState(false);

    const handleReset = () => {
        setResetFlag(!resetFlag);
    };

    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;

        (function () {
            fetchRequests(selectedTab, currentPage, itemsPerPage, filters, columnsState, auth.user?.id_token, signal).then(
                (response) => {
                    setIsLoading(false);
                    setRequests(response.requests);
                }
            ).catch();

            let tab = 0;
            switch (selectedTab) {
                case Tab.Active:
                    tab = 1;
                    break;
                case Tab.Outgoing:
                    tab = 2;
                    break;
                case Tab.Incoming:
                    tab = 3;
                    break;
                case Tab.Draft:
                    tab = 4;
                    break;
            }

            const tabMetricsForm = {
                filters: filters,
                requestsType: tab
            }

            fetchTabMetrics(tabMetricsForm, signal, auth.user?.id_token).then(
                (response) => {
                    setTabMetrics(response);
                }
            ).catch();
        })();
        return () => {
            controller.abort();
        };
    }, [bool]);

    let userInfo = getUserInfo(auth.user?.id_token);

    const getTabId = (itemKey: string) => { return `Selected_${itemKey}`; };

    const buttonId = useId('metrics-callout-button');
    const labelId = useId('metrics-callout-label');
    const descriptionId = useId('metrics-callout-description');

    const handleTabClick = (item?: PivotItem) => {
        if (item) {
            if(item.props.itemKey! === selectedTab) {
                return;
            }
            let columns:IColumn[] = getColumnsByTabName(item.props.itemKey!);
            setFilters(new RequestFilters());
            setIsLoading(true);
            setIsFiltered(false);
            setSelectedTab(item.props.itemKey!);
            setCurrentPage(1);
            setColumnsState(columns);
            setBool(!bool);
            handleReset();
        }
    }

    function _onColumnHeaderClick(ev?: React.MouseEvent<HTMLElement>, column?: IColumn) {
        if(column === undefined) return;

        let isSortedDescending = column.isSortedDescending;

        if (column.isSorted) {
            isSortedDescending = !isSortedDescending;
        }

        let columns = columnsState.map(col => {
            col.isSorted = col.key === column.key;
            if (col.isSorted) {
                col.isSortedDescending = isSortedDescending;
            }
            return col;
        });

        setIsLoading(true);
        setIsFiltered(false);
        setCurrentPage(1);
        setColumnsState(columns);
        setBool(!bool);
    }

    function onClickReset() {
        handleReset();
        setFilters(new RequestFilters());
        setIsLoading(true);
        setIsFiltered(false);
        setCurrentPage(1);
        setBool(!bool);
    }

    function onClickFilter() {
        setIsLoading(true);
        setIsFiltered(true);
        setCurrentPage(1);
        setBool(!bool);
    }

    function _onSendReminder(requestNo: string) {
        sendReminder(requestNo, auth.user?.id_token).then((success) => {
            const messageBarType: MessageBarType = success ? MessageBarType.success : MessageBarType.error;
            const message: string = success ? strings.messageBarReminderSuccess : strings.messageBarReminderError;
            displayMessageBar(messageBarType, message, false);
        }).catch((error) => {
            displayMessageBar(MessageBarType.error, error.message, false);
        });
    }

    function _onCancelRequest(requestNo: number, justification: string) {
        const cancelForm = {
            requestNo: requestNo,
            justification: justification
        };

        cancelOutgoingRequest(cancelForm, auth.user?.id_token).then((success) => {
            const messageBarType: MessageBarType = success ? MessageBarType.success : MessageBarType.error;
            const message: string = success ? strings.messageBarCancelSuccess : strings.messageBarCancelError;
            displayMessageBar(messageBarType, message, false);
            if (success) {
                setIsLoading(true);
                setCurrentPage(1);
                setBool(!bool);
            }
        }).catch((error) => {
            displayMessageBar(MessageBarType.error, error.message, false);
        });
    }

    function _onDeleteDraftRequest(requestNo: number) {
        deleteDraftRequest(requestNo, auth.user?.id_token).then((success) => {
            const messageBarType: MessageBarType = success ? MessageBarType.success : MessageBarType.error;
            const message: string = success ? strings.messageBarDeleteDraftSuccess : strings.messageBarDeleteDraftError;
            displayMessageBar(messageBarType, message, false);
            if (success) {
                setIsLoading(true);
                setCurrentPage(1);
                setBool(!bool);
            }
        })
    }

    function _onExportListClick() {

        const requestsForm = {
            itemsPerPage: 1000,
            page: 1,
            filters: filters,
            sortBy: columnsState.filter(c => c.isSorted)[0].fieldName,
            sortOrder: columnsState.filter(c => c.isSorted)[0].isSortedDescending ? SORT_ORDER_DESC : SORT_ORDER_ASC
        }

        displayMessageBar(MessageBarType.info, strings.messageBarExportListSpinner, true);

        switch (selectedTab) {
            case Tab.Active:
                excelExportActive(requestsForm, strings.activeRequestsExport, auth.user?.id_token).then((success) => {
                    const messageBarType: MessageBarType = success ? MessageBarType.success : MessageBarType.error;
                    const message: string = success ? strings.messageBarExportListSuccess : strings.messageBarExportListError;
                    displayMessageBar(messageBarType, message, false);
                });
                break;
            case Tab.Outgoing:
                excelExportOutgoing(requestsForm, strings.outgoingRequestsExport, auth.user?.id_token).then((success) => {
                    const messageBarType: MessageBarType = success ? MessageBarType.success : MessageBarType.error;
                    const message: string = success ? strings.messageBarExportListSuccess : strings.messageBarExportListError;
                    displayMessageBar(messageBarType, message, false);
                });
                break;
            case Tab.Incoming:
                excelExportIncoming(requestsForm, strings.incomingRequestsExport, auth.user?.id_token).then((success) => {
                    const messageBarType: MessageBarType = success ? MessageBarType.success : MessageBarType.error;
                    const message: string = success ? strings.messageBarExportListSuccess : strings.messageBarExportListError;
                    displayMessageBar(messageBarType, message, false);
                });
                break;
            case Tab.Draft:
                excelExportDraft(requestsForm, strings.draftRequestsExport, auth.user?.id_token).then((success) => {
                    const messageBarType: MessageBarType = success ? MessageBarType.success : MessageBarType.error;
                    const message: string = success ? strings.messageBarExportListSuccess : strings.messageBarExportListError;
                    displayMessageBar(messageBarType, message, false);
                });
                break;
            default:
                break;
        }
    }

    function _onPageChange(selectedItem: number) {
        setIsLoading(true);
        setCurrentPage(selectedItem);
        setBool(!bool);
    }

    function _onChangeItemsPerPage(event: React.FormEvent<HTMLDivElement>, item: IDropdownOption) {
        setIsLoading(true);
        setItemsPerPage(item.key as number);
        setCurrentPage(1);
        setBool(!bool);
    }

    function _onRenderViewMetricsIcon() {
        return <EiopaFnPIcons iconName={"viewMetrics"} iconColor={"#5985AC"}/>;
    }

    const totalItemsForPagination = () => {
        switch (selectedTab) {
            case Tab.Active:
                return tabMetrics.activeRequests;
            case Tab.Outgoing:
                return tabMetrics.outgoingRequests;
            case Tab.Incoming:
                return tabMetrics.incomingRequests;
            case Tab.Draft:
                return tabMetrics.draftRequests;
            default:
                return 0;
        }
    }

    const _onClickViewMetrics = () => {
        if(isMetricsCalloutVisible) {
            setIsMetricsCalloutVisible(false);
        } else {
            setIsActiveMetricsLoading(true);
            setIsMetricsCalloutVisible(true);
            fetchActiveMetrics(auth.user?.id_token).then((resp) => {
                setIsActiveMetricsLoading(false);
                setIsMetricsCalloutVisible(true);
                setActiveMetrics(resp);
            });
        }
    }

    const noResultsMessage = () => {
        if(selectedTab == Tab.Active || !isFiltered) {
            return strings.requestNoResults;
        } else {
            return strings.requestNoResultsFiltered;
        }
    }

    return (
        <Stack>
            <StackItem className={styles.ViewRequestsHeader}>
                {strings.viewRequests}
            </StackItem>
            <StackItem>
                <Stack horizontal style={{justifyContent: "space-between", alignItems: "center"}}>
                    <StackItem>
                        <Pivot
                            aria-label="Main body pivot"
                            selectedKey={selectedTab}
                            // eslint-disable-next-line react/jsx-no-bind
                            onLinkClick={handleTabClick}
                            headersOnly={true}
                            getTabId={getTabId}
                            linkFormat={"links"}
                        >
                            <PivotItem
                                itemKey={Tab.Active}
                                headerText={strings.activeTabLabel}
                                itemCount={tabMetrics.activeRequests}
                            />
                            <PivotItem
                                itemKey={Tab.Outgoing}
                                headerText={strings.outgoingTabLabel}
                                itemCount={tabMetrics.outgoingRequests +
                                    (tabMetrics.isOutgoingPastDueIncluded? "*" : "")}
                            />
                            <PivotItem
                                itemKey={Tab.Incoming}
                                headerText={strings.incomingTabLabel}
                                itemCount={tabMetrics.incomingRequests +
                                    (tabMetrics.isIncomingPastDueIncluded? "*" : "")}
                            />
                            {userInfo.roles.includes(ROLE_CA_END_USER) &&
                                <PivotItem
                                    itemKey={Tab.Draft}
                                    headerText={strings.draftTabLabel}
                                    itemCount={tabMetrics.draftRequests}
                                />
                            }
                        </Pivot>
                    </StackItem>
                    <StackItem>
                        <Stack horizontal>
                            <StackItem>
                                <CommandBarButton
                                    id={buttonId}
                                    text={strings.viewMetricsButtonText}
                                    className={styles.CmdBarButton}
                                    onRenderIcon={() => _onRenderViewMetricsIcon()}
                                    onClick={_onClickViewMetrics}
                                />
                                {isMetricsCalloutVisible && (
                                    <Callout
                                        className={styles.MetricsCallout}
                                        backgroundColor={"#0078D4"}
                                        ariaLabelledBy={labelId}
                                        ariaDescribedBy={descriptionId}
                                        role="dialog"
                                        gapSpace={0}
                                        target={`#${buttonId}`}
                                        onDismiss={()=> setIsMetricsCalloutVisible(false)}
                                    >
                                        {isActiveMetricsLoading?
                                            <Stack className={styles.CalloutBodyLoading}>
                                                <Spinner size={SpinnerSize.medium}/>
                                            </Stack>
                                            :
                                            <Stack className={styles.CalloutBody}>
                                                <StackItem className={styles.CalloutTitle}>
                                                    {strings.viewMetricsActiveRequests}
                                                </StackItem>
                                                <StackItem>
                                                    <Stack horizontal className={styles.CalloutBodyMetrics}>
                                                        <StackItem className={styles.CalloutActiveReqs}>
                                                            {activeMetrics.activeRequests}
                                                        </StackItem>
                                                        <StackItem>
                                                            <div className={styles.CalloutMetricsSmall}>
                                                                <span>{activeMetrics.activeOutgoingRequests} {strings.outgoingTabLabel}</span> / {activeMetrics.pastDueOutgoingRequests} past due
                                                            </div>
                                                            {!userInfo.roles.includes(ROLE_ESA_END_USER) &&
                                                                <div className={styles.CalloutMetricsSmall}>
                                                                    <span>{activeMetrics.activeIncomingRequests} {strings.incomingTabLabel}</span> / {activeMetrics.pastDueIncomingRequests} past due
                                                                </div>
                                                            }
                                                        </StackItem>
                                                    </Stack>
                                                </StackItem>
                                            </Stack>
                                        }
                                    </Callout>
                                )}
                            </StackItem>
                            <StackItem>
                                <CommandBarButton
                                    text={strings.excelExportButtonText}
                                    className={styles.CmdBarButton}
                                    iconProps={{iconName: 'ExcelLogo', className: styles.ExcelLogo}}
                                    onClick={() => _onExportListClick()}
                                />
                            </StackItem>
                        </Stack>
                    </StackItem>
                </Stack>
            </StackItem>
            <StackItem>
                <FilterRequests
                    selectedTab={selectedTab}
                    filters={filters}
                    setFilters={setFilters}
                    onClickReset={onClickReset}
                    onClickFilter={onClickFilter}
                    resetFlag={resetFlag}
                />
            </StackItem>
            <StackItem>
                {
                    isLoading ?
                        <Spinner label={strings.requestResultLoading} size={2} styles={{root: {padding: 20}}}></Spinner>
                        :
                        <FilteredDetailsListWithPagination
                            items={requests}
                            columns={columnsState}
                            onColumnHeaderClick={_onColumnHeaderClick}
                            currentPage={currentPage}
                            onPageChange={_onPageChange}
                            itemsPerPage={itemsPerPage}
                            onChangeItemsPerPage={_onChangeItemsPerPage}
                            totalItems={totalItemsForPagination()}
                            selectedTab={selectedTab}
                            onCancelRequest={_onCancelRequest}
                            onSendReminder={_onSendReminder}
                            onDeleteDraftRequest={_onDeleteDraftRequest}
                            noResultsMessage={noResultsMessage()}
                        />
                }
            </StackItem>
        </Stack>
    );
}

function getColumnsByTabName(selectedTabName: string): IColumn[] {
    switch (selectedTabName) {
        case Tab.Active:
            return _activeColumns;
        case Tab.Outgoing:
            return _outgoingColumns;
        case Tab.Incoming:
            return _incomingColumns;
        case Tab.Draft:
            return _draftColumns;
        default:
            return [];
    }
}

function fetchRequests(selectedTabName: string, page: number, itemsPerPage: number, filters: IRequestFilters, columns: IColumn[], token: string | undefined, signal: AbortSignal):Promise<RequestsWithMetrics> {
    const requestsForm = {
        filters: setDates(filters),
        itemsPerPage: itemsPerPage,
        page: page,
        sortBy: columns.filter(c => c.isSorted)[0].fieldName,
        sortOrder: columns.filter(c => c.isSorted)[0].isSortedDescending ? SORT_ORDER_DESC : SORT_ORDER_ASC
    }
    switch (selectedTabName) {
        case Tab.Active:
            return fetchActiveRequests(requestsForm, token, signal);
        case Tab.Outgoing:
            return fetchOutgoingRequests(requestsForm, token, signal);
        case Tab.Incoming:
            return fetchIncomingRequests(requestsForm, token, signal);
        case Tab.Draft:
            return fetchDraftRequests(requestsForm, token, signal);
        default:
            return Promise.resolve(new RequestsWithMetrics());
    }
}

function setDates(filters:IRequestFilters):IRequestFilters {
    const _filters = new RequestFilters().copyConstructor(filters);
    if(_filters.submissionDateFrom) {
        _filters.submissionDateFrom = fixDate(_filters.submissionDateFrom, "from");
    }
    if(_filters.submissionDateTo) {
        _filters.submissionDateTo = fixDate(_filters.submissionDateTo, "to");
    }
    if(_filters.creationDateFrom) {
        _filters.creationDateFrom = fixDate(_filters.creationDateFrom, "from");
    }
    if(_filters.creationDateTo) {
        _filters.creationDateTo = fixDate(_filters.creationDateTo, "to");
    }
    return _filters;
}

function fixDate(date: Date, dateType: "from" | "to"):Date {
    if(dateType === "from") {
        return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0));
    }
    else {
        return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 0));
    }
}

export default ViewRequests;
